import {
  InfiniteData,
  QueryFunctionContext,
  UseInfiniteQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import moment, { Moment } from 'moment-timezone';

import { queryClient } from '../components/queryClient';
import { useFleet, useHasPermission } from '../hooks';
import { DateTime, Event, EventsFilter } from '../interfaces';
import { EventsService } from '../services';
import { deepCopy, hash } from '../utils';

export const useEventQuery = (eventId: number | string) => {
  const eventIdNumber = typeof eventId === 'string' ? parseInt(eventId, 10) : eventId;
  const queryKey = ['event', eventIdNumber];

  const queryFn = () => EventsService.getEvent(eventIdNumber);

  return useQuery({
    queryKey,
    queryFn,
    enabled: !!eventIdNumber,
    onSettled: (data) => {
      if (data) {
        updateInfiniteEvent(data);
      }
    },
  });
};

/**
 *
 * @param event Event to put into cache
 *
 * @description This function is used to put an event into the query cache,
 * and to invalidate the query, so that the next time the event is requested (via hook)
 * it will be fetched from the server BUT before the server responds, the cached data
 * will be displayed (instantenously) so that the app will feel snappier.
 */
export const setEventQueryCache = (event: Event) => {
  // Put data (or partial data) into cache
  queryClient.setQueryData(['event', event.id], event);

  // in case this event exists in any of the infinite queries (useEventsQuery)
  // find that element in the list by ID and update it
  updateInfiniteEvent(event);

  // Make sure we refetch the event in the background, WHEN NEEDED - but display
  // the cached data in the meantime, instantenously
  queryClient.invalidateQueries(['event', event.id]);
};

export const invalidateEvent = async (eventId: number) => {
  queryClient.invalidateQueries({ queryKey: ['event', eventId], refetchType: 'all' });
};

export const invalidateEventInList = async (eventId: number) => {
  const newEvent = await EventsService.getEvent(eventId);
  setEventQueryCache(newEvent);
};

export const useEventsQuery = (filter: EventsFilter, options: Partial<UseInfiniteQueryOptions> = {}) => {
  const fleet = useFleet();
  const { cursor, group_events, ...notCursor } = filter;
  const queryKey = ['events', { uuid: fleet.uuid, hash: hash(notCursor) }];
  const limit = 25;

  const queryFn = (context: QueryFunctionContext) => {
    const { pageParam, signal } = context;
    const filterWithPage = deepCopy(filter);

    const adjustedFilter = {
      limit,
      order: 'DESC',
      ...filterWithPage,
    };
    adjustedFilter.cursor = pageParam;

    if (group_events) {
      // When using grouped review feature, when we're loading more events, we want to skip all the events
      // that are already in the queue, so we adjust the filter to include them in skipped_event_ids.
      const eventsPages = queryClient.getQueryData<InfiniteData<Event[]> | undefined>(queryKey) ?? [];
      const events: Event[] = eventsPages.pages?.flatMap((page) => page?.events ?? []) ?? [];

      const eventIdsOfAllEventsInTheQueue = new Set(events.map((e) => e.id));
      events.forEach((e) => {
        e.sub_events?.forEach((se) => {
          eventIdsOfAllEventsInTheQueue.add(se.id);
        });
      });

      adjustedFilter.skipped_event_ids = [...(filter.skipped_event_ids ?? []), ...eventIdsOfAllEventsInTheQueue];
      adjustedFilter.order = 'ASC';

      return EventsService.getGroupedEvents(adjustedFilter, fleet, signal);
    }
    return EventsService.getEvents(adjustedFilter, fleet, signal);
  };

  return useInfiniteQuery({
    queryKey,
    queryFn,
    enabled: !!fleet.id,
    cacheTime: 0,
    staleTime: 0,
    ...options,
    getNextPageParam: (lastPage, _allPages) => {
      if (group_events) {
        return lastPage?.events?.length > 0;
      }
      return lastPage?.cursor;
    },
    getPreviousPageParam: (firstPage, _allPages) => firstPage?.cursor,
  });
};

export const updateInfiniteEvent = (event: Event) => {
  queryClient.setQueriesData<InfiniteData<Event[]> | undefined>(
    { queryKey: ['events'], type: 'active' },
    (originalData) => {
      const data = deepCopy(originalData);
      if (data) {
        // eslint-disable-next-line no-param-reassign
        data.pages = (data.pages || []).map((page) => {
          // eslint-disable-next-line no-param-reassign
          page.events = (page.events || []).map((currentEvent) => {
            // @ts-ignore
            if (currentEvent.id === event.id) {
              const newEvent = deepCopy(event);
              newEvent.updated_at = new Date().toISOString();
              if (newEvent.tags === undefined) {
                newEvent.tags = currentEvent.tags;
              }
              return newEvent;
            }
            return currentEvent;
          });
          return page;
        });
        return data;
      }
      return originalData;
    },
  );
};

// These two queries are used to navigate the list of portal list events from the viewer modal,
// (like when using arrow keys) -
export const useEventsListEvents = () => {
  const queryKey = ['eventsListEvents'];

  const queryFn = () => Promise.resolve<Event[]>([]);

  return useQuery({
    queryKey,
    queryFn,
  });
};

export const setEventsListEvents = (events: Event[]) => {
  queryClient.setQueryData(['eventsListEvents'], events);
};

export const useEventRouteFeedback = (eventId: number) => {
  const queryFn = () => EventsService.getRouteFeedback(eventId);
  const queryKey = ['eventRouteFeedback', eventId];
  const hasPermission = useHasPermission('events:read:route_feedback');

  return useQuery({
    queryKey,
    queryFn,
    enabled: hasPermission && !!eventId,
  });
};

export const useExpirationDateForGroups = () => {
  const queryKey = ['expirationDateForGroups'];

  const queryFn = () => Promise.resolve<Moment | null>(null);

  return useQuery({
    queryKey,
    queryFn,
    staleTime: Infinity, // never considered stale
    cacheTime: Infinity, // never garbage collected
  });
};

export const setExpirationDateForGroups = (timeString: DateTime) => {
  const date = moment(timeString);
  queryClient.setQueryData(['expirationDateForGroups'], date);
};
